import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Send, Users, Clock } from "lucide-react";
import api from "@/lib/api";

/**
 * Admin settings — configure who receives the 20h daily justification email
 * (leads sem tratamento + leads perdidos que precisam de justificação).
 */
export default function DailyReportConfigSettings() {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [c, u] = await Promise.all([
        api.get("/daily-report-config"),
        api.get("/users"),
      ]);
      setCfg(c.data);
      setUsers(u.data?.users || u.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/daily-report-config", {
        team_responsible_user_id: cfg.team_responsible_user_id,
        team_responsible_name: cfg.team_responsible_name,
        team_responsible_email: cfg.team_responsible_email,
        untreated_sla_minutes: Number(cfg.untreated_sla_minutes) || 60,
        send_hour_lisbon: Number(cfg.send_hour_lisbon) || 20,
        active: !!cfg.active,
      });
      setCfg(r.data);
      toast.success("Configuração guardada.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const triggerNow = async () => {
    setTriggering(true);
    try {
      const r = await api.post("/daily-report-config/trigger");
      if (r.data?.ok) {
        toast.success(`Email enviado para ${r.data.sent_to} · ${r.data.pending_count} lead(s)`);
      } else if (r.data?.skipped) {
        toast.info(`Sem envio: ${r.data.skipped}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao disparar");
    } finally {
      setTriggering(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-3xl mx-auto space-y-6" data-testid="drc-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Relatório Diário</div>
        <h1 className="font-display text-4xl tracking-tight text-foreground">
          Justificação diária às 20h
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          De segunda a sábado às 20h (hora de Lisboa) enviamos ao <b>responsável de equipa</b> um email
          com o link para justificar todos os leads que ficaram <b>sem tratamento em tempo útil</b> ou foram
          <b> perdidos</b> nesse dia.
        </p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="drc-active"
            checked={!!cfg.active}
            onChange={(e) => setCfg({ ...cfg, active: e.target.checked })}
            className="mt-1"
          />
          <div>
            <div className="text-sm font-semibold text-foreground">Envio automático activo</div>
            <div className="text-xs text-muted-foreground">Desactive para pausar o email das 20h sem apagar a configuração.</div>
          </div>
        </label>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2">
            <Users className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2} />
            Responsável pela equipa
          </label>
          <select
            data-testid="drc-user"
            value={cfg.team_responsible_user_id || ""}
            onChange={(e) => {
              const uid = e.target.value || null;
              const u = users.find(x => x.id === uid);
              setCfg({
                ...cfg,
                team_responsible_user_id: uid,
                team_responsible_name: u?.name || null,
                team_responsible_email: u?.email || null,
              });
            }}
            className="w-full bg-card border border-border rounded-xl px-4 py-2.5 text-sm"
          >
            <option value="">— escolher utilizador —</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
            ))}
          </select>
          {cfg.team_responsible_email && (
            <div className="text-xs text-muted-foreground mt-2">
              Vai receber os emails em: <b>{cfg.team_responsible_email}</b>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2">
              <Clock className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={2} />
              Hora de envio (Lisboa)
            </label>
            <input
              type="number"
              min="0"
              max="23"
              data-testid="drc-hour"
              value={cfg.send_hour_lisbon}
              onChange={(e) => setCfg({ ...cfg, send_hour_lisbon: Number(e.target.value) })}
              className="w-full bg-card border border-border rounded-xl px-4 py-2.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2">
              Limite SLA (minutos)
            </label>
            <input
              type="number"
              min="5"
              max="480"
              data-testid="drc-sla"
              value={cfg.untreated_sla_minutes}
              onChange={(e) => setCfg({ ...cfg, untreated_sla_minutes: Number(e.target.value) })}
              className="w-full bg-card border border-border rounded-xl px-4 py-2.5 text-sm"
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Leads que não têm primeiro contacto após este limite entram na lista de justificação.
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-border">
          <button
            onClick={triggerNow}
            disabled={triggering || !cfg.team_responsible_email}
            data-testid="drc-trigger"
            className="inline-flex items-center gap-2 bg-card hover:bg-muted/40 border border-border text-foreground rounded-full px-5 py-2 text-sm font-medium disabled:opacity-40"
            title={!cfg.team_responsible_email ? "Escolha primeiro um responsável" : "Enviar agora"}
          >
            {triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Enviar teste agora
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="drc-save"
            className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background rounded-full px-6 py-2 text-sm font-medium disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>

        {cfg.last_sent_date && (
          <div className="text-xs text-muted-foreground pt-2">
            Último envio: <b>{cfg.last_sent_date}</b>
            {cfg.last_sent_count != null && <> · <b>{cfg.last_sent_count}</b> lead(s)</>}
          </div>
        )}
      </div>
    </div>
  );
}
